import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Maps, Text } from '@hopskipdrive/react-components';
import { formatInTimeZone } from 'date-fns-tz';

import { colorTokens } from '../../../../shared/components/variables';
import { estimatedTimeFromNowWithin24HoursSlim } from '../../../../shared/utils/helpers/util';
import { useLiveDriverUpdates } from '../../../hooks/useLiveDriverUpdates';
import { useRideDetailsQuery } from '../../../hooks/useRideDetailsQuery';
import { Rider } from '../../../types/rideOverview';

import { CareDriverRoute } from './CareDriverRoute';
import {
  DriverLiveDetailsCard,
  DriverSummaryContainer,
  EtaContainer,
  EtaDiffTag,
} from './DriverPositionDetails.styles';

interface DriverPositionDetailsProps {
  ridersInCar: Rider[];
}

export const DriverPositionDetails: FC<DriverPositionDetailsProps> = ({ ridersInCar }) => {
  const { rideSlug } = useParams();
  const { data: rideDetails } = useRideDetailsQuery({ rideSlug: rideSlug || '' });
  const isInProgress = ['in_progress', 'waiting'].includes(rideDetails?.data?.state);
  const hasCheckedInOrInProgress = ['in_progress', 'waiting', 'claimed', 'checked_in'].includes(
    rideDetails?.data?.state
  );
  const { lastCoordinatesDateTime, driverCoordinatesChanged } = useLiveDriverUpdates(
    isInProgress,
    rideDetails?.data?.id
  );
  const driverLastCoordinateReceivedAt = lastCoordinatesDateTime || rideDetails?.data?.lastCoordinateReceivedAt;
  const estimatedDiffToEta = estimatedTimeFromNowWithin24HoursSlim(rideDetails?.data?.etaDetails?.eta || null);
  const actualRoutePolyline = rideDetails?.data?.mapDetails?.actualRoutePolyline;
  // if ride is complete or canceled, calculate the persisted polyline
  const actualPolyline =
    !hasCheckedInOrInProgress &&
    actualRoutePolyline &&
    window.google?.maps?.geometry.encoding.decodePath(actualRoutePolyline);

  return (
    <DriverSummaryContainer>
      {hasCheckedInOrInProgress && driverLastCoordinateReceivedAt && (
        <DriverLiveDetailsCard>
          <Text variant="h6" textColor="primaryText">
            {'Last Coordinate received from driver at'}
          </Text>
          <Text variant="body1" fontWeight={300} textColor="primaryText">
            {formatInTimeZone(
              driverLastCoordinateReceivedAt || new Date(),
              rideDetails?.data?.timeZone || 'America/Los_Angeles',
              'h:mm aa zzz'
            )}
          </Text>
        </DriverLiveDetailsCard>
      )}
      {hasCheckedInOrInProgress && rideDetails?.data?.etaDetails && (
        <DriverLiveDetailsCard>
          <Text variant="h6" textColor="primaryText">
            {`Driver ETA ${rideDetails?.data?.etaDetails?.description}`}
          </Text>
          <EtaContainer $status={rideDetails?.data?.etaDetails?.etaStatus || ''}>
            <Text variant="body1" fontWeight={300} textColor="primaryText">
              {`${formatInTimeZone(
                rideDetails?.data?.etaDetails?.eta || new Date(),
                rideDetails?.data?.timeZone || 'America/Los_Angeles',
                'h:mm zzz'
              )}${estimatedDiffToEta}`}
            </Text>
          </EtaContainer>
          {rideDetails?.data?.etaDetails?.timeDifference && (
            <EtaDiffTag $status={rideDetails?.data?.etaDetails?.etaStatus || ''}>
              {`${rideDetails?.data?.etaDetails?.timeDifference} ${rideDetails?.data?.etaDetails?.etaStatus}`}
            </EtaDiffTag>
          )}
        </DriverLiveDetailsCard>
      )}

      {actualPolyline ? (
        <Maps.Polyline
          data-testid="persisted-driver-polyline"
          options={{
            path: actualPolyline,
            geodesic: true,
            strokeColor: colorTokens.primary.light,
            strokeOpacity: 1.0,
            strokeWeight: 4,
            zIndex: 2,
          }}
        />
      ) : (
        <CareDriverRoute
          rideActualPolyline={rideDetails?.data?.mapDetails?.actualRoutePolyline}
          liveCoordinates={driverCoordinatesChanged?.data || []}
          shouldHideCarMarker={!isInProgress}
          ridersInDriverCar={ridersInCar}
        />
      )}
    </DriverSummaryContainer>
  );
};
